import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class DevErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('React Error Boundary caught error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Call external error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // In production, show minimal error UI
      if (!import.meta.env.DEV) {
        return (
          <div className="min-h-screen flex items-center justify-center p-4">
            <Alert variant="destructive" className="max-w-lg">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription className="space-y-4">
                <p>An unexpected error occurred. Please refresh the page.</p>
                <Button onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        );
      }

      // In development, show detailed error
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
          <div className="max-w-4xl mx-auto space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle className="text-lg font-bold">
                React Error Boundary
              </AlertTitle>
              <AlertDescription>
                A React component error was caught
              </AlertDescription>
            </Alert>

            <div className="bg-white dark:bg-gray-800 rounded-lg border p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-lg mb-2">Error Message</h3>
                <div className="bg-red-50 dark:bg-red-950/20 border-l-4 border-red-500 p-4 rounded">
                  <code className="text-sm text-red-700 dark:text-red-300">
                    {this.state.error.message}
                  </code>
                </div>
              </div>

              {this.state.error.stack && (
                <div>
                  <h3 className="font-semibold text-lg mb-2">Stack Trace</h3>
                  <pre className="bg-gray-900 text-red-400 p-4 rounded overflow-x-auto text-xs font-mono">
                    {this.state.error.stack}
                  </pre>
                </div>
              )}

              {this.state.errorInfo?.componentStack && (
                <div>
                  <h3 className="font-semibold text-lg mb-2">Component Stack</h3>
                  <pre className="bg-gray-900 text-orange-400 p-4 rounded overflow-x-auto text-xs font-mono">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button onClick={this.handleReset}>
                  Try Again
                </Button>
                <Button variant="outline" onClick={() => window.location.reload()}>
                  Reload Page
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
